import {
  customMarkerApi,
  floorApi,
  markerApi,
  sitesApi,
  stationsApi,
} from '@/api/operations'
import GoogleMap from '@/components/google-map'
import { useAuthContext } from '@/features/auth/context'
import { NotifyService } from '@/helpers'
import useCache from '@/hooks/useCache'
import { useDebounce } from '@/hooks/useDebounce'
import { useSignalR } from '@/signalr/context'
import {
  ActionSignalR,
  CUSTOM_MARKER,
  LOCATION_FOR_MAPVIEW,
  MARKER,
  SITE_FOR_MAPVIEW,
  SignalRConnection,
  getGroupName,
} from '@/types'
import { Floor } from '@/types/floor'
import { SiteOverlapDetail, TypeFloorOverlay } from '@/types/siteOverlap'
import { STATION_DETAIL, STATION_DETAIL_MARKER } from '@/types/stations'
import { getCustomMarkerSVG } from '@/ultils'
import {
  getStationMarkerIcon,
  getStationMarkerByStatus,
} from './components/LayersMenu/helper'

import { HubConnection } from '@microsoft/signalr'
import { Box, Stack } from '@mui/material'
import _ from 'lodash'
import { useEffect, useRef, useState } from 'react'
import {
  MapViewOverlay,
  MapViewOverlayOptions,
} from '@/components/google-map/components'
import classes from './styles.module.scss'
import {
  StationUpdateSignalR,
  stationUpdateItemId,
} from '@/types/SignalR/station'

const ZOOM_DEFAULT = 19
const ZOOM_LOCATION = 16
const ZOOM_STATION_MAX = 19

interface DrawLocationsProps {
  redraw?: boolean
}

interface LocationOverlayState {
  siteId: number
  locationId: number
  floorId: number
  floor: Floor
  overlayId: number
  overlayTypeId: number
}

interface MarkerStationState {
  locationId: number
  floorId: number
  station: STATION_DETAIL
}

enum MAP_TYPE {
  VIEW = 'VIEW',
  CUSTOM_MARKER = 'CUSTOM_MARKER',
}

const MapViewRightMenu = () => {
  const { interfaceId } = useAuthContext()

  const sites = useRef<SITE_FOR_MAPVIEW[]>([])
  const sitesDraw = useRef<MapViewOverlay[]>([])
  const locationsDraw = useRef<MapViewOverlay[]>([])
  const activeFloorRef = useRef<Floor>()
  const activeOverlayTypeRef = useRef<TypeFloorOverlay>(
    TypeFloorOverlay.OverlayWithAllAnnotations,
  )

  const stationsDraw = useRef<google.maps.Marker[]>([])
  const infoHoverStationDraw = useRef<google.maps.InfoWindow>()
  const map = useRef<google.maps.Map | null>(null)
  const startZoom = useRef<any>(null)
  const suppressChangeBounds = useRef<boolean>(true)

  const viewTypeRef = useRef(MAP_TYPE.VIEW)
  const customMarkersDraw = useRef<google.maps.Marker[]>([])

  const [showChangePipe, setShowChangePipe] = useState<boolean>(false)
  const showChangePipeRef = useRef(showChangePipe)

  const groupName = getGroupName(interfaceId!)
  const connectionRef = useRef<HubConnection | null>(null)

  const mapOutput = (value: google.maps.Map) => {
    map.current = value
    map.current.setZoom(ZOOM_DEFAULT)
  }

  const onSetShowChangePipe = (value: boolean) => {
    showChangePipeRef.current = value
    setShowChangePipe(value)
  }

  useEffect(() => {
    const fetchMarker = async () => {
      const _markers = await markerApi
        .getAll()
        .then(({ data }) => data)
        .catch((e) => {
          NotifyService.error(e)
          return [] as MARKER[]
        })
    }
    fetchMarker()
  }, [])

  useEffect(() => {
    const fetchSites = async () => {
      const data = await sitesApi
        .getSites(Number(interfaceId))
        .then((res) => res.data.data)
        .catch((e) => {
          NotifyService.error(e)
          return [] as SITE_FOR_MAPVIEW[]
        })
      sites.current = data
      drawSites()
    }
    if (interfaceId) fetchSites()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interfaceId])

  const createOverlayBounds = (data: {
    bottomLeftLatitude: number
    bottomLeftLongitude: number
    topRightLatitude: number
    topRightLongitude: number
  }) => {
    const swBound = new google.maps.LatLng(
      data.bottomLeftLatitude,
      data.bottomLeftLongitude,
    )
    const neBound = new google.maps.LatLng(
      data.topRightLatitude,
      data.topRightLongitude,
    )
    const bounds = new google.maps.LatLngBounds(swBound, neBound)
    return bounds
  }

  const drawOverlay = (
    data: SiteOverlapDetail,
    options?: MapViewOverlayOptions,
  ) => {
    const swBound = new google.maps.LatLng(
      data.bottomLeftLatitude,
      data.bottomLeftLongitude,
    )
    const neBound = new google.maps.LatLng(
      data.topRightLatitude,
      data.topRightLongitude,
    )
    const overlay: MapViewOverlay = new MapViewOverlay(
      swBound,
      neBound,
      data.imageUrl,
      data.rotate,
      map.current as any,
      options,
    )
    return overlay
  }

  const hideAllOverlay = (data: MapViewOverlay[]) => {
    data.forEach((item) => item.hide())
  }

  const showAllOverlay = (data: MapViewOverlay[]) => {
    data.forEach((item) => item.show())
  }

  const clearSites = () => {
    if (!map.current) return
    sitesDraw.current.forEach((item) => item.clear())
    sitesDraw.current = []
  }

  const drawSites = () => {
    if (!map.current) return
    clearAll()
    onSetCurrentBounds()
  }

  const getLocations = () => {
    return sites.current.reduce(
      (rest: LOCATION_FOR_MAPVIEW[], item) => rest.concat(item.locations),
      [],
    )
  }

  const onSetCurrentBounds = (callback?: () => void) => {
    suppressChangeBounds.current = true
    if (sitesDraw.current.length) {
      const bounds = sitesDraw.current.map((s) => s.getBounds())
      const first = bounds.shift() as google.maps.LatLngBounds
      const fitBounds = new google.maps.LatLngBounds(
        first.getSouthWest(),
        first.getNorthEast(),
      )
      bounds.forEach((item) => fitBounds.union(item))
      map.current?.fitBounds(fitBounds)
    } else {
      const getOneLocation = getLocations()[0]
      if (!getOneLocation) {
        suppressChangeBounds.current = false
        return
      }
      const locationsBounds = [getOneLocation].reduce(
        (rest: google.maps.LatLngBounds[], item) => {
          const { overlay, floor } = getFloorOverlay(item)
          if (overlay && floor) rest.push(createOverlayBounds(overlay))
          return rest
        },
        [],
      )
      if (locationsBounds.length) {
        const first = locationsBounds.shift() as google.maps.LatLngBounds
        const fitBounds = new google.maps.LatLngBounds(
          first.getSouthWest(),
          first.getNorthEast(),
        )
        locationsBounds.forEach((item) => fitBounds.union(item))
        map.current?.fitBounds(fitBounds)
      } else if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          const currentPosition = new google.maps.LatLng(
            position.coords.latitude,
            position.coords.longitude,
          )
          map.current?.setCenter(currentPosition)
        })
      }
    }
    setTimeout(() => {
      const zoom = map.current?.getZoom() || 0
      startZoom.current = zoom
      if (zoom > ZOOM_DEFAULT) map.current?.setZoom(ZOOM_DEFAULT)
      suppressChangeBounds.current = false
      callback && callback()
    }, 200)
  }

  const clearLocations = () => {
    if (!map.current) return
    locationsDraw.current.forEach((item) => item.clear())
    locationsDraw.current = []
    onClearFloors()
    clearStations()
    clearCustomMarkers()
    setLocationsShowing([])
  }

  const getFloorOverlay = (data: LOCATION_FOR_MAPVIEW) => {
    let floor = data.floors.find((it) => it.floor.isDefault)
    if (activeFloorRef.current?.locationId === data.location.id) {
      floor = data.floors.find(
        (item) => activeFloorRef.current?.id === item.floor.id,
      )
    }
    const overlay = floor?.overlays?.find(
      (item) => item.typeId === activeOverlayTypeRef.current,
    )
    const overlayWithoutPipes = floor?.overlays?.find(
      (item) => item.typeId === TypeFloorOverlay.OverlayWithoutPipes,
    )
    return {
      floor,
      overlay,
      overlayWithoutPipes,
    }
  }

  const onSetActiveFloor = (data?: Floor) => {
    if (data?.id === activeFloorRef.current?.id) return
    activeFloorRef.current = data
  }

  const onClearFloors = () => {
    onSetActiveFloor(undefined)
  }

  const getAreaOfBounds = (bounds: google.maps.LatLngBounds) => {
    const projection = map.current?.getProjection() as google.maps.Projection
    const topRight = projection.fromLatLngToPoint(
      bounds.getNorthEast(),
    ) as google.maps.Point
    const bottomLeft = projection.fromLatLngToPoint(
      bounds.getSouthWest(),
    ) as google.maps.Point
    const width = topRight.x - bottomLeft.x
    const height = bottomLeft.y - topRight.y
    return {
      width,
      height,
    }
  }

  const checkShowStations = (siteId: number) => {
    const zoom = map.current?.getZoom() ?? 0
    if (!map.current || zoom < ZOOM_LOCATION) return false
    if (zoom >= ZOOM_STATION_MAX) return true
    const site = sites.current.find((it) => it.site.id === siteId)
    const locations = site?.locations || []
    const overlays = locations
      .map((it) =>
        it.floors
          .find((temp) => temp.floor.isDefault)
          ?.overlays?.find(
            (temp) =>
              temp.typeId === TypeFloorOverlay.OverlayWithAllAnnotations,
          ),
      )
      .filter((it) => it) as SiteOverlapDetail[]
    if (!overlays.length) return false
    const bounds = createOverlayBounds(overlays[0])
    overlays.forEach((it) => {
      bounds.union(createOverlayBounds(it))
    })
    const viewArea = getAreaOfBounds(
      map.current?.getBounds() as google.maps.LatLngBounds,
    )
    const areaSite = getAreaOfBounds(bounds)
    return (
      areaSite.width >= viewArea.width / 2 ||
      areaSite.height >= viewArea.height / 2
    )
  }

  const onClickLocation = async (data: LocationOverlayState) => {
    if (!data.locationId || activeFloorRef.current?.id === data.floorId) return
    const _floors = await floorApi
      .getByLocationId(`${data.locationId}`, { requiredOverylay: 1 })
      .then(({ data }) => data)
      .catch((e) => {
        NotifyService.error(e)
        return undefined
      })
    onSetActiveFloor(_floors?.find((item) => item.isDefault))
  }

  const [locationsShowing, setLocationsShowing] = useState<
    LOCATION_FOR_MAPVIEW[]
  >([])

  const drawLocations = ({ redraw }: DrawLocationsProps = {}) => {
    if (!map.current) return
    let locations: LOCATION_FOR_MAPVIEW[] = []
    const mapBounds = map.current?.getBounds()
    let _showChangePipe = false
    getLocations().forEach((item) => {
      const { overlay, floor, overlayWithoutPipes } = getFloorOverlay(item)
      if (overlay && floor) {
        const bounds = createOverlayBounds(overlay)
        if (mapBounds?.intersects(bounds)) {
          locations.push({
            ...item,
            overlay,
            floor,
          })
          if (overlayWithoutPipes) _showChangePipe = true
        }
      }
    })
    setLocationsShowing(locations)
    if (
      !_showChangePipe &&
      showChangePipeRef.current &&
      activeOverlayTypeRef.current !==
        TypeFloorOverlay.OverlayWithAllAnnotations
    ) {
      NotifyService.error(
        'There is not any pipe overlay of this location. The map overlay is set to default',
      )
      onSetShowChangePipe(_showChangePipe)
      onSetActiveOverlayType()
      drawLocations()
      return
    } else onSetShowChangePipe(_showChangePipe)
    if (activeFloorRef.current) {
      const locationExited = locations.find(
        (t) => t.location.id === activeFloorRef.current?.locationId,
      )
      if (!locationExited) onClearFloors()
    }
    if (redraw) clearLocations()
    else {
      const itemsDrawed = locationsDraw.current.filter((item) => {
        const state = item.getState<LocationOverlayState>()
        const insideWillDraw = locations.find(
          (it) => it.location.id === state.locationId,
        ) // location inside list location will draw
        const conditionActiveFloor =
          !!activeFloorRef.current &&
          (activeFloorRef.current.id === state.floorId ||
            activeFloorRef.current.locationId !== state.locationId) // have active floor === floor or !== location
        const conditionNoActionFloor =
          !activeFloorRef.current && state.floor.isDefault // do not have active floor and location showing is floor default
        const conditionOverlayType =
          activeOverlayTypeRef.current === state.overlayTypeId
        return (
          insideWillDraw &&
          (conditionActiveFloor || conditionNoActionFloor) &&
          conditionOverlayType
        )
      })
      const itemsHide = locationsDraw.current.filter(
        (item) =>
          !itemsDrawed.find(
            (it) =>
              it.getState<LocationOverlayState>().overlayId ===
              item.getState<LocationOverlayState>().overlayId,
          ),
      )
      locations = locations.filter(
        (s) =>
          !itemsDrawed.find(
            (t) =>
              t.getState<LocationOverlayState>().locationId === s.location.id,
          ),
      )
      hideAllOverlay(itemsHide)
      showAllOverlay(itemsDrawed.filter((it) => !it.isShow()))
    }
    locations.forEach((item) => {
      if (item.overlay && item.floor) {
        const state: LocationOverlayState = {
          siteId: item.location.siteId,
          locationId: item.location.id,
          overlayId: item.overlay.id,
          overlayTypeId: item.overlay.typeId,
          floorId: item.floor.floor.id,
          floor: item.floor.floor,
        }
        const _location = drawOverlay(item.overlay, {
          state,
          onClick: () => onClickLocation(state),
        })
        locationsDraw.current.push(_location)
      }
    })
    if (!activeFloorRef.current) {
      const centerMap = map.current.getCenter() as google.maps.LatLng
      const locationCenter = locationsDraw.current.find(
        (it) => it.isShow() && it.getBounds().contains(centerMap),
      )
      if (locationCenter) onClickLocation(locationCenter.getState())
    }
  }

  const onGetStations = async (locationId: number, floorId?: number) => {
    const data = await stationsApi
      .getAll(Number(interfaceId), locationId, floorId)
      .then((res) => res.data)
      .catch((e) => {
        NotifyService.error(e)
        return [] as STATION_DETAIL[]
      })
    return data
  }

  const clearStations = () => {
    stationsDraw.current.forEach((t) => t.setMap(null))
    stationsDraw.current = []
  }

  const getInfoWindowHover = (station: STATION_DETAIL) => {
    const content = `<div class="station-hover-box"">
      ${
        !!station.pictureUrl
          ? `<img src="${station.pictureUrl}"  alt="${station.pictureFileName}"/>`
          : ''
      }
      <div class="name">${station.description}</div>
    </div>`
    if (infoHoverStationDraw.current) {
      infoHoverStationDraw.current.setContent(content)
    } else {
      infoHoverStationDraw.current = new google.maps.InfoWindow({
        content: content,
        minWidth: 182,
        maxWidth: 182,
      })
      google.maps.event.addListener(
        infoHoverStationDraw.current,
        'domready',
        function () {
          const el = document.querySelector(`.station-hover-box`) as HTMLElement
          const parent =
            el?.parentNode?.parentNode?.parentNode?.parentNode?.parentNode
              ?.firstElementChild
          if (parent && !parent.classList.contains('custom-infor-station')) {
            parent.classList.add('custom-infor-station')
          }
        },
      )
    }
    infoHoverStationDraw.current?.set('stationId', station.id)
    return infoHoverStationDraw
  }

  const getLocationShowing = () => {
    const bounds = map.current?.getBounds()
    return locationsDraw.current.filter(
      (s) => bounds?.intersects(s.getBounds()) && s.isShow(),
    )
  }

  const { getItem: getStationsCache, setItem: setStationsCache } = useCache<{
    locationId: number
    floorId: number
    stations: STATION_DETAIL[]
  }>(300)

  const [stationsShowing, setStationsShowing] = useState<google.maps.Marker[]>(
    [],
  )

  const getInfoMarkerHover = (station: any) => {
    const content = `<div class="station-hover-box">
      <div class="name" style="white-space: pre-wrap">${station.note}</div>
    </div>`
    if (infoHoverStationDraw.current) {
      infoHoverStationDraw.current.setContent(content)
    } else {
      infoHoverStationDraw.current = new google.maps.InfoWindow({
        content: content,
        minWidth: 182,
        maxWidth: 182,
      })
      google.maps.event.addListener(
        infoHoverStationDraw.current,
        'domready',
        function () {
          const el = document.querySelector(`.station-hover-box`) as HTMLElement
          const parent =
            el?.parentNode?.parentNode?.parentNode?.parentNode?.parentNode
              ?.firstElementChild
          if (parent && !parent.classList.contains('custom-infor-station')) {
            parent.classList.add('custom-infor-station')
          }
        },
      )
    }
    infoHoverStationDraw.current?.set('stationId', station.id)
    return infoHoverStationDraw
  }

  const drawStations = async ({ redraw }: { redraw?: boolean } = {}) => {
    if (!locationsDraw.current.length) return
    const { locations } = getLocationsOfStationWillShow()
    if (locations?.length) {
      const raws = await Promise.all(
        locations.map(async (it) => {
          const state = it.getState<LocationOverlayState>()
          const stationCache = getStationsCache(
            (temp) =>
              temp.floorId === state.floorId &&
              temp.locationId === state.locationId,
          )
          if (stationCache) return stationCache.stations
          const stations = await onGetStations(state.locationId, state.floorId)
          setStationsCache(
            {
              floorId: state.floorId,
              locationId: state.locationId,
              stations: stations,
            },
            (temp) =>
              temp.floorId === state.floorId &&
              temp.locationId === state.locationId,
          )
          return stations
        }),
      )
      const stations: MarkerStationState[] = locations.reduce(
        (rest: MarkerStationState[], item, index) => {
          const state = item.getState<LocationOverlayState>()
          return rest.concat(
            raws[index].map((temp) => ({
              floorId: state.floorId,
              locationId: state.locationId,
              station: temp,
            })),
          )
        },
        [],
      )
      let markersWillDraw: MarkerStationState[] = []
      if (redraw) {
        clearStations()
        markersWillDraw = stations
      } else {
        const markersDrawed: google.maps.Marker[] = []
        stations.forEach((item) => {
          const drawed = stationsDraw.current.find((temp) => {
            const state: MarkerStationState = temp.get('customData')
            return state.station.id === item.station.id
          })
          if (drawed) markersDrawed.push(drawed)
          else markersWillDraw.push(item)
        })

        stationsDraw.current.forEach((item) => {
          const stateItem: MarkerStationState = item.get('customData')
          const keep = markersDrawed.find((temp) => {
            const stateTemp: MarkerStationState = temp.get('customData')
            return stateItem.station.id === stateTemp.station.id
          })
          if (!keep) item.setVisible(false)
        })
      }
      markersWillDraw.forEach((item) => {
        const marker = new google.maps.Marker({
          map: map.current,
          zIndex: 500,
          icon: getStationMarkerIcon(item.station.marker),
          position: new google.maps.LatLng(
            item.station.grsCoordinatedLatitude,
            item.station.grsCoordinatedLongitude,
          ),
        })
        marker.set('customData', item)
        marker.addListener('mouseover', function () {
          const temp: MarkerStationState = marker.get('customData')
          const inforWindow = getInfoWindowHover(temp.station)
          inforWindow.current?.open(map.current, marker)
        })
        marker.addListener('mouseout', function () {
          const stationId = infoHoverStationDraw.current?.get('stationId')
          if (stationId === item.station.id)
            infoHoverStationDraw.current?.close()
        })

        stationsDraw.current.push(marker)
      })
      const bounds = map.current?.getBounds() ?? new google.maps.LatLngBounds()
      const visibleMarkers = stationsDraw.current.filter((marker) => {
        const position = marker.getPosition() ?? new google.maps.LatLng(0, 0)
        return marker.getVisible() && bounds.contains(position)
      })
      setStationsShowing(visibleMarkers)
    } else {
      clearStations()
    }
  }

  const clearCustomMarkers = () => {
    customMarkersDraw.current.forEach((t) => t.setMap(null))
    customMarkersDraw.current = []
  }

  const getCustomMarkers = async (floorId: number) => {
    const data = await customMarkerApi
      .getAll({ interfaceId: Number(interfaceId), floorId })
      .then((res) => res.data)
      .catch((err) => {
        NotifyService.error(err)
        return [] as CUSTOM_MARKER[]
      })
    return data
  }

  const drawCustomMarkers = async ({ redraw }: { redraw?: boolean } = {}) => {
    if (!locationsDraw.current.length) {
      clearCustomMarkers()
      return
    }
    const { locations } = getLocationsOfStationWillShow()
    if (locations?.length) {
      const raws = await Promise.all(
        locations.map(async (item) => {
          const state = item.getState<LocationOverlayState>()
          const customMakersData = await getCustomMarkers(state.floorId)
          return customMakersData
        }),
      )
      const data = raws.reduce(
        (rest: CUSTOM_MARKER[], item) => rest.concat(item),
        [],
      )
      let markersWillDraw: CUSTOM_MARKER[] = []
      if (redraw) {
        clearCustomMarkers()
        markersWillDraw = data
      } else {
        const markersDrawed: google.maps.Marker[] = []
        data.forEach((item) => {
          const drawed = customMarkersDraw.current.find((temp) => {
            const customMarkerId = temp.get('customMarkerId')
            return customMarkerId === item.id
          })
          if (drawed) markersDrawed.push(drawed)
          else markersWillDraw.push(item)
        })

        customMarkersDraw.current.forEach((item) => {
          const keep = markersDrawed.find(
            (temp) => item.get('customMarkerId') === temp.get('customMarkerId'),
          )
          if (!keep) item.setVisible(false)
        })
      }
      markersWillDraw.forEach((item) => {
        const marker = new google.maps.Marker({
          map: map.current,
          zIndex: 600,
          icon: getCustomMarkerSVG(item.shortName),
          position: new google.maps.LatLng(
            item.coordinatedLatitude,
            item.coordinatedLongitude,
          ),
        })
        marker.set('customMarkerId', item.id)
        marker.set('customMarker', item)
        marker.addListener('mouseover', function () {
          const temp: MarkerStationState = marker.get('customMarker')
          const inforWindow = getInfoMarkerHover(temp)
          inforWindow.current?.open(map.current, marker)
        })
        marker.addListener('mouseout', function () {
          const stationId = infoHoverStationDraw.current?.get('stationId')
          if (stationId === item?.id) infoHoverStationDraw.current?.close()
        })
        customMarkersDraw.current.push(marker)
      })
    } else clearCustomMarkers()
  }

  const getLocationsOfStationWillShow = () => {
    const location = getLocationShowing()
    const siteIds = _.uniq(
      location.map((it) => it.getState<LocationOverlayState>().siteId),
    )
    const siteIdsWillShow = siteIds.filter((siteId) =>
      checkShowStations(siteId),
    )
    return {
      siteIds: siteIdsWillShow,
      locations: location.filter((it) =>
        siteIdsWillShow.includes(it.getState<LocationOverlayState>().siteId),
      ),
    }
  }

  const onRedrawMap = useDebounce(() => {
    if (
      !map.current ||
      viewTypeRef.current === MAP_TYPE.CUSTOM_MARKER ||
      suppressChangeBounds.current
    )
      return
    const zoom = map.current.getZoom() || 0
    if (zoom + 1 >= startZoom.current) {
      // hide site level
      hideAllOverlay(sitesDraw.current)
      // show location level
      drawLocations()
      drawStations()
      drawCustomMarkers()
      // hide station level
      // clearStations()
      // clearCustomMarkers()
    } else {
      // show site level
      showAllOverlay(sitesDraw.current)
      // hide location level
      onClearFloors()
      hideAllOverlay(locationsDraw.current)
      setLocationsShowing([])
      setStationsShowing([])
      // hide station level
      clearStations()
      clearCustomMarkers()
      let _sitesDraw
      if (zoom + 2 === startZoom.current) {
        _sitesDraw = [...(sites.current || [])].filter((item) => item.overlay)
        _sitesDraw.forEach((item) => {
          const _site = drawOverlay(item.overlay, {
            state: item,
          })
          sitesDraw.current.push(_site)
        })
      }
    }
  }, 500)

  const clearAll = () => {
    clearSites()
    onClearFloors()
    clearLocations()
    map.current?.setZoom(ZOOM_DEFAULT)
  }

  useEffect(() => {
    if (!map.current) return
    const boundsChangedEvent = google.maps.event.addListener(
      map.current,
      'bounds_changed',
      onRedrawMap,
    )
    return () => {
      google.maps.event.removeListener(boundsChangedEvent)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSetActiveOverlayType = (
    type: TypeFloorOverlay = TypeFloorOverlay.OverlayWithAllAnnotations,
  ) => {
    if (type === activeOverlayTypeRef.current) return
    activeOverlayTypeRef.current = type
  }

  const { getHubConnect } = useSignalR()

  useEffect(() => {
    let connection: HubConnection | undefined
    const startSignalR = async () => {
      connection = await getHubConnect()
      connectionRef.current = connection
      await connection.invoke(SignalRConnection.JOIN_GROUP, groupName)
      connection.on(
        ActionSignalR.STATION_UPDATE,
        (stationUpdate: StationUpdateSignalR) => {
          // Payload is { interfaceId, items: [{ id, status }] } — resolve the
          // marker locally from the 1-based status.
          const { interfaceId, items } = stationUpdate
          if (!items.length) return
          items.forEach((item) => {
            const stationId = stationUpdateItemId(item)
            const { status } = item
            const marker = getStationMarkerByStatus(status)
            const station = stationsDraw.current?.find((it) => {
              const data: MarkerStationState = it.get('customData')
              return (
                data.station.id === Number(stationId) &&
                data.station.interfaceId === interfaceId
              )
            })
            if (!station) return
            station.setIcon(getStationMarkerIcon(marker))
            const data: MarkerStationState = station.get('customData')
            data.station.marker = marker
            station.set('customData', data)
            const stationCache = getStationsCache(
              (temp) =>
                temp.floorId === data.floorId &&
                temp.locationId === data.locationId,
            )
            if (stationCache) {
              const i = stationCache.stations.findIndex(
                (it) => it.id === data.station.id,
              )
              if (i !== -1) {
                stationCache.stations[i].marker = marker
                setStationsCache(
                  stationCache,
                  (temp) =>
                    temp.floorId === data.floorId &&
                    temp.locationId === data.locationId,
                )
              }
            }
            const { locations } = getLocationsOfStationWillShow()
            const locationShowing = locations.find(
              (it) =>
                it.getState<LocationOverlayState>().locationId ===
                data.locationId,
            )
            if (!locationShowing) return
          })
        },
      )
    }

    startSignalR()

    return () => {
      if (connectionRef.current) {
        connectionRef.current.off(ActionSignalR.STATION_UPDATE)
        connectionRef.current.invoke(SignalRConnection.LEAVE_GROUP, groupName)
      }
    }
    // eslint-disable-next-line
  }, [getHubConnect])

  return (
    <Box sx={{ width: '408px', height: ' 100%', position: 'relative' }}>
      <Stack className={classes.root} direction="row" flexGrow={1}>
        <Stack className={classes.root}>
          <GoogleMap currentPosition={false} mapOutput={mapOutput} />
          {/* map view layer */}
        </Stack>
      </Stack>
    </Box>
  )
}

export default MapViewRightMenu
